'use client'

import React, { useRef, useState, useEffect } from 'react'
import { apiGetAllKeywords, apiSearchResult } from '@/apis/user'
import { ellipse, getBaseImageUrl, getCategoryHref, getMerchantHref } from '@/constants/hooks'
import { SearchCategories, SearchMerchant } from '@/services/dataTypes'
import { usePathname, useRouter } from 'next/navigation'
import Link from 'next/link'
import Image from 'next/image'

const C = { PRIMARY: '#F8F246', SECONDARY: '#2CBBC3', YELLOW: '#FF9934', BROWN: '#8C5826', LIGHT: '#E1F1F2' }

interface Props {
    companyId?: string
    mer_slug?: string
    slug_type?: string
    cat_slug?: string
    domain?: string
}

const SearchBar = ({ companyId = '', mer_slug = '', slug_type = '', cat_slug = '', domain = '' }: Props) => {
    const [search, setSearch] = useState('')
    const [isDropdownVisible, setIsDropdownVisible] = useState(false)
    const [tagsData, setTagsData] = useState<string[]>([])
    const [categoriesData, setCategoriesData] = useState<SearchCategories[]>([])
    const [merchantData, setMerchantData] = useState<SearchMerchant[]>([])
    const [errMsg, setErrMessage] = useState({ show: false, msg: '' })
    const [mounted, setMounted] = useState(false)
    const pathName = usePathname()
    const router = useRouter()
    const inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => { setMounted(true) }, [])

    // Load keywords
    useEffect(() => {
        if (!companyId) return
        apiGetAllKeywords(companyId)
            .then(response => {
                if (response.data) setTagsData(response.data)
            })
            .catch(console.error)
    }, [companyId])

    // Search function
    const handleSearch = async () => {
        if (!companyId) return
        try {
            if (search.trim().length >= 3) {
                const response = await apiSearchResult(search, companyId)
                if (response.data?.merchants?.length > 0 || response.data?.categories?.length > 0) {
                    setCategoriesData(response.data?.categories || [])
                    setMerchantData(response.data?.merchants || [])
                    setErrMessage({ show: false, msg: '' })
                } else {
                    setCategoriesData([])
                    setMerchantData([])
                    setErrMessage({ show: true, msg: 'No result found.' })
                }
            } else {
                setCategoriesData([])
                setMerchantData([])
                setErrMessage({ show: true, msg: 'Input should be at least three characters long.' })
            }
        } catch (err) {
            console.error('Search error:', err)
        }
    }

    // Auto search on typing (1 second delay)
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (search.trim() === '') {
                setMerchantData([])
                setCategoriesData([])
                setErrMessage({ show: false, msg: '' })
            } else {
                handleSearch()
            }
        }, 1000)
        return () => clearTimeout(timeoutId)
    }, [search, companyId])

    // Close dropdown on search page
    useEffect(() => {
        if (pathName === '/search') setIsDropdownVisible(false)
    }, [pathName])

    const handleSearchSubmit = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && search.trim()) {
            e.preventDefault()
            router.push(`/search?query=${encodeURIComponent(search.trim())}`)
            setIsDropdownVisible(false)
        }
    }

    if (!mounted) {
        return (
            <div className="relative w-full" style={{ fontFamily: "'Inter', sans-serif" }}>
                <div className="relative flex items-center">
                    <input
                        type="text"
                        placeholder="Search deals, stores..."
                        className="w-full pl-4 pr-10 py-2 text-xs sm:text-sm rounded-full"
                        style={{ background: 'white', border: `1px solid ${C.BROWN}30` }}
                        disabled
                        value=""
                    />
                </div>
            </div>
        )
    }

    return (
        <div className="relative w-full" style={{ fontFamily: "'Inter', sans-serif" }}>
            <div className="relative flex items-center">
                <input
                    type="text"
                    placeholder="Search deals, stores..."
                    className="w-full pl-4 pr-10 py-2 text-xs sm:text-sm rounded-full focus:outline-none text-gray-800 placeholder-gray-400 shadow-sm transition-all"
                    style={{ background: 'white', border: `1px solid ${C.BROWN}30` }}
                    ref={inputRef}
                    value={search || ''}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={handleSearchSubmit}
                    onBlur={() => setTimeout(() => setIsDropdownVisible(false), 150)}
                    onFocus={() => setIsDropdownVisible(pathName !== '/search')}
                />

                <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                        if (search.trim()) {
                            router.push(`/search?query=${encodeURIComponent(search.trim())}`)
                            setIsDropdownVisible(false)
                        }
                    }}
                    aria-label="Search"
                    className="absolute right-1 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full flex items-center justify-center transition-all duration-300 shadow-sm hover:scale-105 cursor-pointer group/btn"
                    style={{ background: C.BROWN }}
                >
                    <svg className="w-3.5 h-3.5 text-white group-hover/btn:scale-110 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                </button>
            </div>

            {isDropdownVisible && (
                <div className="absolute top-full right-0 mt-2 rounded-2xl p-4 z-[9999] w-[min(480px,90vw)] max-h-[450px] overflow-y-auto"
                    style={{ background: 'white', border: `1px solid ${C.PRIMARY}30`, boxShadow: '0 8px 32px rgba(0,0,0,0.12)', overflow: 'visible' }}>

                    {tagsData.length > 0 && (
                        <div className="mb-4 pb-3" style={{ borderBottom: `1px solid ${C.PRIMARY}20` }}>
                            <h5 className="text-[10px] font-black uppercase tracking-widest mb-2" style={{ color: C.BROWN }}>Popular Tags</h5>
                            <div className="flex flex-wrap gap-1.5">
                                {tagsData.slice(0, 20).map((item: string, i: number) => (
                                    <Link
                                        key={i}
                                        href={`/search?query=${item}`}
                                        className="px-2.5 py-1 rounded-lg text-xs font-medium"
                                        style={{ background: `${C.PRIMARY}30`, color: C.BROWN }}
                                    >
                                        {item}
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}

                    {errMsg.show && (
                        <p className="text-sm mb-4" style={{ color: C.YELLOW }}>{errMsg.msg}</p>
                    )}

                    {merchantData.length > 0 && (
                        <div className="mb-4 pb-3" style={{ borderBottom: `1px solid ${C.PRIMARY}20` }}>
                            <h5 className="text-[10px] font-black uppercase tracking-widest mb-2" style={{ color: C.BROWN }}>Stores</h5>
                            <div className="grid grid-cols-3 gap-2">
                                {merchantData.map((item, i) => (
                                    <Link
                                        key={i}
                                        href={getMerchantHref(item, mer_slug, slug_type)}
                                        className="flex flex-col items-center p-2 rounded-xl hover:bg-orange-50"
                                        style={{ background: '#fafafa' }}
                                    >
                                        <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-1" style={{ background: '#f5f5f5' }}>
                                            <Image
                                                src={getBaseImageUrl(domain, item.merchant_logo, '')}
                                                alt={item.merchant_name}
                                                width={32}
                                                height={32}
                                                className="object-contain"
                                            />
                                        </div>
                                        <span className="text-xs font-medium text-center line-clamp-1" style={{ color: C.BROWN }}>
                                            {ellipse(item.merchant_name, 18)}
                                        </span>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}

                    {categoriesData.length > 0 && (
                        <div>
                            <h5 className="text-[10px] font-black uppercase tracking-widest mb-2" style={{ color: C.BROWN }}>Categories</h5>
                            <div className="flex flex-wrap gap-1.5">
                                {categoriesData.map((item, i) => (
                                    <Link
                                        key={i}
                                        href={getCategoryHref(item, cat_slug, slug_type)}
                                        className="px-2.5 py-1 rounded-lg text-xs font-medium"
                                        style={{ background: `${C.SECONDARY}30`, color: C.BROWN }}
                                    >
                                        {item.name}
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

export default SearchBar